import { Controller, useFormContext } from 'react-hook-form';
import { Typography } from '@mui/material';
import { PageLayout, Button, SettingItem } from 'shared-ui';

import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import { DefinitionsBar } from '../definitionsBar/DefinitionsBar';
import { Input } from 'ui/input/Input';
import { Form } from 'ui/form/Form';

import * as Styled from './MailAccount.styles';
import { MailAccountFormBody, MailAccountProps } from './MailAccount.types';

export const MailAccount = ({ onSubmit, isLoading }: MailAccountProps) => {
  const { formatMessage } = useLocale();

  const {
    handleSubmit,
    control,
    formState: { errors, isValid },
  } = useFormContext<MailAccountFormBody>();

  const authenticationOptions = [
    { value: 'SSL/TLS', label: formatMessage({ id: AppMessages['definitions.mailAccount.authentication.ssl'] }) },
    { value: 'STARTTLS', label: formatMessage({ id: AppMessages['definitions.mailAccount.authentication.starttls'] }) },
    { value: 'None', label: formatMessage({ id: AppMessages['definitions.mailAccount.authentication.none'] }) },
  ];

  return (
    <PageLayout
      header={formatMessage({ id: AppMessages['definitions.title'] })}
      renderContent={() => (
        <>
          <DefinitionsBar />

          <SettingItem
            header={
              <Typography variant="h4">
                {formatMessage({ id: AppMessages['definitions.mailAccount.smtpServer.title'] })}
              </Typography>
            }
          >
            <Styled.Form onSubmit={handleSubmit(onSubmit)}>
              <Controller
                name="address"
                render={({ field }) => (
                  <Input
                    {...field}
                    value={field.value || ''}
                    label={formatMessage({ id: AppMessages['definitions.mailAccount.address.label'] })}
                    placeholder={formatMessage({ id: AppMessages['definitions.mailAccount.address.placeholder'] })}
                    errorMessage={errors?.address?.message}
                    required
                  />
                )}
              />

              <Controller
                name="port"
                render={({ field }) => (
                  <Input
                    {...field}
                    type="number"
                    value={field.value ?? ''}
                    label={formatMessage({ id: AppMessages['definitions.mailAccount.port.label'] })}
                    placeholder={formatMessage({ id: AppMessages['definitions.mailAccount.port.placeholder'] })}
                    errorMessage={errors?.port?.message}
                  />
                )}
              />

              <div />

              <Controller
                name="username"
                render={({ field }) => (
                  <Input
                    {...field}
                    value={field.value || ''}
                    label={formatMessage({ id: AppMessages['definitions.mailAccount.username.label'] })}
                    placeholder={formatMessage({ id: AppMessages['definitions.mailAccount.username.placeholder'] })}
                    errorMessage={errors?.username?.message}
                    autoComplete="off"
                  />
                )}
              />

              <Controller
                name="password"
                render={({ field }) => (
                  <Input
                    {...field}
                    value={field.value || ''}
                    label={formatMessage({ id: AppMessages['definitions.mailAccount.password.label'] })}
                    placeholder={formatMessage({ id: AppMessages['definitions.mailAccount.password.placeholder'] })}
                    type="password"
                    errorMessage={errors?.password?.message}
                    autoComplete="new-password"
                  />
                )}
              />

              <div />

              <Form.ControlledSelect
                name="authentication"
                control={control}
                label={formatMessage({ id: AppMessages['definitions.mailAccount.authentication.label'] })}
                options={authenticationOptions}
                required
              />

              <Styled.SubmitWrapper>
                <Button
                  isLoading={isLoading}
                  variant="contained"
                  color="primary"
                  type="submit"
                  size="small"
                  disabled={!isValid}
                >
                  {formatMessage({ id: AppMessages['common.save'] })}
                </Button>
              </Styled.SubmitWrapper>
            </Styled.Form>
          </SettingItem>
        </>
      )}
    />
  );
};
